/**
 * /en/admin/structure/paragraphs_type/appointment_form/fields
 *
 * See this page for form requirements: https://modea.atlassian.net/wiki/spaces/LH/pages/4136435782/Appointment+Form+Requirements+Testing
 */
import CLASS_NAMES from "@js/constants/css-constants.es6.js";
import { ATTRIBUTES, DOM_EVENTS, HTML_ELEMENTS } from "@js/constants/html-constants.es6.js";
import { SEARCH_PARAM_KEYS, SESSION_STORAGE_KEYS } from "@js/constants/js-constants.es6.js";
import { RESULT_TYPES, VIEW_MODES } from "@js/constants/drupal-constants.es6.js";
import {
  ACCESSIBILITY_TYPE,
  INSURANCE_REQUIREMENT,
  REFERRAL_REQUIREMENT,
} from "@js/constants/epic-constants.es6.js";

import Tab from "@js/helpers/tab.es6.js";
import Address from "@js/helpers/address.es6.js"
import Phone from "@js/helpers/phone.es6.js"
import PatientDemographics from "@js/helpers/patient-demographics.es6.js"
import Insurance from "@js/helpers/insurance.es6.js"

import SessionStorageUtils from "@js/utils/session-storage-utils.es6.js"
import MyChartUser from "@js/mychart-user.es6.js"
import ConfirmationUtils from "@js/confirmation-utils.es6.js"
import HttpClient from "@js/helpers/http-client.es6.js";
import EloquaServiceUtils from "@js/utils/eloqua-service-utils.es6.js";
import LuminisServiceUtils from "@js/utils/luminis-service-utils.es6.js";
import OverlayService from "@js/overlay-service.es6.js";
import DateUtils from "@js/utils/date-utils.es6.js";

import Element from "@js/core/element.es6.js";
import Field from "@js/core/field.es6.js";
import LinkField from "@js/core/link-field.es6.js";
import FormParagraph from "@js/paragraphs/form-paragraph.es6.js";

import LegendFormElementParagraphField from "@js/fields/form-element-legend-paragraph-field.es6.js"
import CheckboxFormElementParagraphField from "@js/fields/form-element-checkbox-paragraph-field.es6.js"
import EmailFormElementParagraphField from "@js/fields/form-element-email-paragraph-field.es6.js"
import FileFormElementParagraphField from "@js/fields/form-element-file-paragraph-field.es6.js"
import NumberFormElementParagraphField from "@js/fields/form-element-number-paragraph-field.es6.js"
import RadioFormElementParagraphField from "@js/fields/form-element-radio-paragraph-field.es6.js"
import SelectFormElementParagraphField from "@js/fields/form-element-select-paragraph-field.es6.js"
import TelephoneFormElementParagraphField from "@js/fields/form-element-telephone-paragraph-field.es6.js"
import TextFormElementParagraphField from "@js/fields/form-element-text-paragraph-field.es6.js"
import TextareaFormElementParagraphField from "@js/fields/form-element-textarea-paragraph-field.es6.js"
import MercuryHealthInformationBlockField from "@js/blocks/mercury-health-information-block-field.es6.js"
import PatientInsuranceOptionsParagraphField from "@js/fields/patient-insurance-options-paragraph-field.es6.js";
import VirtualUrgentCareTermsAndConditionsModalParagraphField from "@js/fields/virtual-urgent-care-terms-and-conditions-modal-paragraph-field.es6.js"

/**
 * The URL alias for the "I'll use insurance" option of the "Insurance Method"
 * taxonomy must be set to this value in Drupal.
 * See: /en/admin/structure/taxonomy/manage/insurance_method/overview
 */
const INSURANCE_METHOD_TAXONOMY_USE_INSURANCE_ALIAS = 'ill-use-insurance';

/**
 * The URL alias for the "Yes" option of the "Yes and No" taxonomy must be set
 * to this value in Drupal.
 * See: /en/admin/structure/taxonomy/manage/yes_and_no/overview
 */
const YES_AND_NO_TAXONOMY_YES_ALIAS = 'yes';

export default class AppointmentFormParagraph extends FormParagraph {
  #luminisServiceUtils;

  #overlayService;

  #myChartUser;

  constructor({ parentElement }) {
    super({
      machineName: 'appointment_form',
      parentElement,
    });

    this.#luminisServiceUtils = new LuminisServiceUtils();
    this.#overlayService = new OverlayService();
  }

  get addGenderInformation() {
    return new Field({
      machineName: 'field_add_gender_information_lab',
      parentElement: this,
    });
  }

  get addPreferredName() {
    return new Field({
      machineName: 'field_add_a_preferred_name_text',
      parentElement: this,
    });
  }

  get careTargetTabs() {
    return new Element({
      selector: '.patient-information-section > .tab-wrapper',
      parentElement: this,
    });
  }

  get dayTimeBoxWrapper() {
    return new Element({
      selector: '.day-time-box-wrapper',
      parentElement: this,
    });
  }

  get disclaimerInsurance() {
    return new Field({
      machineName: 'field_insurance_disclaimer',
      parentElement: this,
    });
  }

  get disclaimerVisitReason() {
    return new Field({
      machineName: 'field_visit_reason_disclaimer',
      parentElement: this,
    });
  }

  get heading3LoggedInAs() {
    return new Field({
      machineName: 'field_h3_6',
      parentElement: this,
    });
  }

  get inputDayNumber() {
    return new NumberFormElementParagraphField({
      machineName: 'field_form_element_day_number',
      parentElement: this,
    });
  }

  get inputCity() {
    return new TextFormElementParagraphField({
      machineName: 'field_form_element_city',
      parentElement: this,
    });
  }

  get inputCondition() {
    return new SelectFormElementParagraphField({
      machineName: 'field_form_element_condition',
      parentElement: this,
    });
  }

  get inputEmail() {
    return new EmailFormElementParagraphField({
      machineName: 'field_form_element_email',
      parentElement: this,
    });
  }

  get inputFirstName() {
    return new TextFormElementParagraphField({
      machineName: 'field_form_element_first_name',
      parentElement: this,
    });
  }

  get inputGenderInformation() {
    return new TextareaFormElementParagraphField({
      machineName: 'field_form_element_gender_info',
      parentElement: this,
    });
  }

  get inputInsuranceCardBackImage() {
    return new FileFormElementParagraphField({
      machineName: 'field_form_element_ins_back_img',
      parentElement: this,
    });
  }

  get inputInsuranceCardFrontImage() {
    return new FileFormElementParagraphField({
      machineName: 'field_form_element_ins_front_img',
      parentElement: this,
    });
  }

  get inputInsuranceGroupNumber() {
    return new TextFormElementParagraphField({
      machineName: 'field_form_element_ins_group_num',
      parentElement: this,
    });
  }

  get inputInsuranceMemberId() {
    return new TextFormElementParagraphField({
      machineName: 'field_form_element_ins_member_id',
      parentElement: this,
    });
  }

  get inputInsuranceProvider() {
    return new SelectFormElementParagraphField({
      machineName: 'field_form_element_ins_provider',
      parentElement: this,
    });
  }

  get inputInsuranceSelection() {
    return new RadioFormElementParagraphField({
      machineName: 'field_form_element_ins_selection',
      parentElement: this,
    });
  }

  get inputInterpreterNeed() {
    return new RadioFormElementParagraphField({
      machineName: 'field_form_element_interpreter',
      parentElement: this,
    });
  }

  get inputLastName() {
    return new TextFormElementParagraphField({
      machineName: 'field_form_element_last_name',
      parentElement: this,
    });
  }

  get inputLocationAcknowledgement() {
    return new CheckboxFormElementParagraphField({
      machineName: 'field_form_element_location_ackn',
      parentElement: this,
    });
  }

  get inputMonthNumber() {
    return new NumberFormElementParagraphField({
      machineName: 'field_form_element_month_number',
      parentElement: this,
    });
  }

  get inputPhoneNumber() {
    return new TelephoneFormElementParagraphField({
      machineName: 'field_form_element_phone_number',
      parentElement: this,
    });
  }

  get inputPhoneType() {
    return new SelectFormElementParagraphField({
      machineName: 'field_form_element_phone_type',
      parentElement: this,
    });
  }

  get inputPreferredContactMethod() {
    return new RadioFormElementParagraphField({
      machineName: 'field_form_element_contact_metho',
      parentElement: this,
    });
  }

  get inputPreferredName() {
    return new TextFormElementParagraphField({
      machineName: 'field_form_element_preferred_nam',
      parentElement: this,
    });
  }

  get inputReferralAcknowledgement() {
    return new CheckboxFormElementParagraphField({
      machineName: 'field_form_element_referral_ackn',
      parentElement: this,
    });
  }

  get inputReferralImage() {
    return new FileFormElementParagraphField({
      machineName: 'field_form_element_referral_img',
      parentElement: this,
    });
  }

  get inputReferralRequirement() {
    return new RadioFormElementParagraphField({
      machineName: 'field_form_element_referral_requ',
      parentElement: this,
    });
  }

  get inputSex() {
    return new RadioFormElementParagraphField({
      machineName: 'field_form_element_sex',
      parentElement: this,
    });
  }

  get inputState() {
    return new SelectFormElementParagraphField({
      machineName: 'field_form_element_state',
      parentElement: this,
    });
  }

  get inputStreetAddress() {
    return new TextFormElementParagraphField({
      machineName: 'field_form_element_street_addr',
      parentElement: this,
    });
  }

  get inputVisitDetails() {
    return new TextareaFormElementParagraphField({
      machineName: 'field_form_element_visit_details',
      parentElement: this,
    });
  }

  get inputVisitType() {
    return new RadioFormElementParagraphField({
      machineName: 'field_form_element_visit_type',
      parentElement: this,
    });
  }

  get inputYearNumber() {
    return new NumberFormElementParagraphField({
      machineName: 'field_form_element_year_number',
      parentElement: this,
    });
  }

  get inputZipCode() {
    return new TextFormElementParagraphField({
      machineName: 'field_form_element_zip_code',
      parentElement: this,
    });
  }

  get insuranceDetails() {
    return new Element({
      selector: '.insurance-details',
      parentElement: this,
    });
  }

  get legendDateOfBirth() {
    return new LegendFormElementParagraphField({
      machineName: 'field_form_element_legend_2',
      parentElement: this,
    });
  }

  get legendFullName() {
    return new LegendFormElementParagraphField({
      machineName: 'field_form_element_legend',
      parentElement: this,
    });
  }

  get legendInsuranceCardImages() {
    return new LegendFormElementParagraphField({
      machineName: 'field_form_element_legend_3',
      parentElement: this,
    });
  }

  get linkGenericProviderError() {
    return new LinkField({
      machineName: 'field_provider_epic_error_page',
      parentElement: this,
    });
  }

  get linkNoAvailability() {
    return new LinkField({
      machineName: 'field_no_availability_link',
      parentElement: this,
    });
  }

  get locationAddressBox() {
    return new Element({
      selector: '.location-box .location-address',
      parentElement: this,
    });
  }

  get locationBoxWrapper() {
    return new Element({
      selector: '.location-box-wrapper',
      parentElement: this,
    });
  }

  get locationNameBox() {
    return new Element({
      selector: '.location-box .location-name',
      parentElement: this,
    });
  }

  get loginCard() {
    return new Field({
      machineName: 'field_card',
      parentElement: this,
    });
  }

  get logoutLink() {
    return new Element({
      selector: '.log-out-link',
      parentElement: this,
    });
  }

  get mercuryHealthBlock() {
    return new MercuryHealthInformationBlockField({
      machineName: 'field_mercury_health_block',
      parentElement: this,
    });
  }

  get sectionAppointmentDetails() {
    return new Element({
      selector: '.appointment-details-section',
      parentElement: this,
    });
  }

  get sectionPatientInformation() {
    return new Element({
      selector: '.patient-information-section',
      parentElement: this,
    });
  }

  static get referralJumpLink() {
    return new Element({
      selector: '[href="#referral-information"]',
    });
  }

  get sectionPatientInformationLoggedIn() {
    return new Element({
      selector: '.patient-information-section--logged-in',
      parentElement: this,
    });
  }

  get sectionPatientInformationLoggedOut() {
    return new Element({
      selector: '.patient-information-section--logged-out',
      parentElement: this,
    });
  }

  get sectionReferralInformation() {
    return new Element({
      selector: '.referral-information-section',
      parentElement: this,
    });
  }

  get patientInsuranceOptions() {
    return new PatientInsuranceOptionsParagraphField({
      machineName: 'field_patient_insurance_options',
      parentElement: this,
    });
  }

  get tabGetCareForMySelf() {
    return new Field({
      machineName: 'field_text_button',
      parentElement: this,
    });
  }

  get tabGetCareForSomeoneElse() {
    return new Field({
      machineName: 'field_text_button_2',
      parentElement: this,
    });
  }

  get timeBox() {
    return new Element({
      selector: '.day-time-box .time-box',
      parentElement: this,
    });
  }

  get timeJoiningText() {
    return new Field({
      machineName: 'field_time_joining_text',
      parentElement: this,
    });
  }

  get usernameLink() {
    return new Element({
      selector: '.username-link',
      parentElement: this,
    });
  }

  get virtualUrgentCareTermsAndConditionsModal() {
    return new VirtualUrgentCareTermsAndConditionsModalParagraphField({
      overlayService: this.#overlayService,
      machineName: 'field_terms_and_conditions_modal',
      parentElement: this,
    });
  }

  async initialize() {
    this.#myChartUser = await MyChartUser.getLoggedInUser();

    if (this.viewMode === VIEW_MODES.DEFAULT) {
      this.#updateInputCondition();
      this.#updateInputVisitType();
      this.#updateLocation();
      this.#updateHeading3DayAndTime();
    }

    await this.#updatePatientInformation();

    this.#updateAddPreferredName();
    this.#updateAddGenderInformation();
    this.#updateInputVisitDetails();

    if (this.viewMode === VIEW_MODES.VIRTUAL_URGENT_CARE_OPEN) {
      this.#updateInputInterpreterNeed();
    }

    this.#updateInputInsuranceSelection();
    this.#updateInputInsuranceCardFrontImage();
    this.#updateInputInsuranceCardBackImage();
    this.#updateInputInsuranceProvider();
    this.#updateInputInsuranceMemberId();
    this.#updateInputReferralRequirement();
    this.#hidePhoneTypeVUC();
    this.#updateInputLocationAcknowledgement();
    this.updateInputSubmit();

    this.updateInsuranceRequirements();
    this.updateReferralRequirements();
  }

  #updateInputCondition() {
    this.inputCondition.paragraph.toggleRequirement(true);
  }

  #updateInputVisitType() {
    this.inputVisitType.paragraph.toggleRequirement(true);

    const appointmentType = SessionStorageUtils.getItem(
      SESSION_STORAGE_KEYS.APPOINTMENT_TYPE,
    );
    ConfirmationUtils.setVisitType(appointmentType);
  }

  #updateLocation() {
    const appointmentLocation = SessionStorageUtils.getItem(
      SESSION_STORAGE_KEYS.APPOINTMENT_LOCATION,
    );

    if (appointmentLocation) {
      const apptJson = JSON.parse(appointmentLocation);

      if (apptJson) {
        this.locationNameBox.$().text(apptJson.title);
        this.locationAddressBox.$().text(apptJson.address);
      }
    }
  }

  #updateCareTargetTabs() {
    const $getCareForMyselfTab = this.tabGetCareForMySelf.$(0);
    const $getCareForSomeoneElse = this.tabGetCareForSomeoneElse.$(0);

    new Tab({
      $tabElement: $getCareForMyselfTab,
      selected: true,
      selectionHandler: () => this.#onCareTargetTabSelected(true),
    });

    new Tab({
      $tabElement: $getCareForSomeoneElse,
      selected: false,
      selectionHandler: () => this.#onCareTargetTabSelected(false),
    });
  }

  #onCareTargetTabSelected(useAuthenticatedPatient) {
    const fieldsToHide = [];
    const fieldsToShow = [];

    if (useAuthenticatedPatient) {
      fieldsToHide.push(this.sectionPatientInformationLoggedOut);
      fieldsToShow.push(this.sectionPatientInformationLoggedIn);
      fieldsToShow.push(this.patientInsuranceOptions);
    } else {
      fieldsToHide.push(this.sectionPatientInformationLoggedIn);
      fieldsToShow.push(this.sectionPatientInformationLoggedOut);
      fieldsToHide.push(this.patientInsuranceOptions);
    }

    fieldsToHide.forEach((field) => field.$().attr(ATTRIBUTES.HIDDEN, true));
    fieldsToShow.forEach((field) => field.$().removeAttr(ATTRIBUTES.HIDDEN));

    const requireDemographics = !useAuthenticatedPatient;
    this.#updateDemographicRequirements(requireDemographics);
  }

  #updateHeading3DayAndTime() {
    const reservedTime = SessionStorageUtils.getItem(
      SESSION_STORAGE_KEYS.RESERVED_TIME_SLOT,
    );

    if (reservedTime) {
      const date = new Date(reservedTime);

      const localString = date
        .toLocaleTimeString('en-US', {
          month: 'long',
          weekday: 'long',
          hour: 'numeric',
          minute: '2-digit',
        })
        .split(' ');

      const timeJoiningText = this.timeJoiningText.item
        .$()
        .text()
        .trim();

      const time = `${localString[localString.length - 2]} ${
        localString[localString.length - 1]
      }`;

      this.timeBox.$().text(`${timeJoiningText} ${time}`);
    }
  }

  async #updatePatientInformation() {
    const showField = field => field.$().removeAttr(ATTRIBUTES.HIDDEN);
    const hideField = field => field.$().attr(ATTRIBUTES.HIDDEN, true);

    const showCareTargetTabs = this.#showCareTargetTabs();
    if (showCareTargetTabs) {
      this.#updateCareTargetTabs();
      showField(this.careTargetTabs);
    } else {
      hideField(this.careTargetTabs);
    }

    if (this.#myChartUser) {
      this.#updateHeading3LoggedInAs();
      this.#updateUsernameLink();
      this.#updateLinkLogOut();

      hideField(this.loginCard);
      showField(this.sectionPatientInformationLoggedIn);
      hideField(this.sectionPatientInformationLoggedOut);
    } else {
      this.#updateDemographicRequirements(true);

      showField(this.loginCard);
      showField(this.sectionPatientInformationLoggedOut);
      hideField(this.sectionPatientInformationLoggedIn);
    }
  }

  #updateDemographicRequirements(required) {
    const requiredFields = [
      this.inputDayNumber,
      this.inputCity,
      this.inputEmail,
      this.legendDateOfBirth,
      this.legendFullName,
      this.inputFirstName,
      this.inputLastName,
      this.inputMonthNumber,
      this.inputPhoneNumber,
      this.inputPhoneType,
      this.inputSex,
      this.inputState,
      this.inputStreetAddress,
      this.inputYearNumber,
      this.inputZipCode,
    ];

    requiredFields.forEach(field =>
      field.paragraph.toggleRequirement(required),
    );
  }

  #showCareTargetTabs() {
    const loggedIn = !!this.#myChartUser;

    const accessibilityType = SessionStorageUtils.getItem(
      SESSION_STORAGE_KEYS.ACCESSIBILITY_TYPE,
    );

    const isVirtualUrgentCare =
      this.viewMode.toUpperCase() ===
      VIEW_MODES.VIRTUAL_URGENT_CARE_OPEN.toUpperCase();

    if (isVirtualUrgentCare) {
      return loggedIn;
    }

    const isOpenSchedulingAppointment =
      accessibilityType?.toUpperCase() ===
      ACCESSIBILITY_TYPE.OPEN.toUpperCase();

    return loggedIn && isOpenSchedulingAppointment;
  }

  #updateHeading3LoggedInAs() {
    const $heading3LoggedInAs = this.heading3LoggedInAs.$();
    const currentHeadingText = $heading3LoggedInAs.text();
    $heading3LoggedInAs.text(
      `${currentHeadingText} ${this.#myChartUser.fullName}`,
    );
  }

  #updateUsernameLink() {
    this.usernameLink.$().text(this.#myChartUser.firstName);
  }

  #updateLinkLogOut() {
    this.logoutLink
      .$()
      .on(`${DOM_EVENTS.CLICK} ${DOM_EVENTS.KEYPRESS}`, () =>
        MyChartUser.logout(),
      );
  }

  #updateAddPreferredName() {
    const $addPreferredName = this.addPreferredName.$();
    const $inputPreferredName = this.inputPreferredName.$();

    $addPreferredName.on(`${DOM_EVENTS.CLICK} ${DOM_EVENTS.KEYPRESS}`, () => {
      $addPreferredName.addClass(CLASS_NAMES.HIDDEN);
      $inputPreferredName.removeClass(CLASS_NAMES.HIDDEN);
    });
  }

  #updateAddGenderInformation() {
    const $addGenderInformation = this.addGenderInformation.$();
    const $inputGenderInformation = this.inputGenderInformation.$();

    $addGenderInformation.on(
      `${DOM_EVENTS.CLICK} ${DOM_EVENTS.KEYPRESS}`,
      () => {
        $addGenderInformation.addClass(CLASS_NAMES.HIDDEN);
        $inputGenderInformation.removeClass(CLASS_NAMES.HIDDEN);
      },
    );
  }

  updateAppointmentNotesRequired() {
    this.#updateInputVisitDetails();
  }

  #updateInputVisitDetails() {
    if (this.viewMode === VIEW_MODES.VIRTUAL_URGENT_CARE_OPEN) {
      this.inputVisitDetails.paragraph.toggleRequirement(true);
    } else {
      const apptNotesReq =
        SessionStorageUtils.getItem(
          SESSION_STORAGE_KEYS.APPOINTMENT_NOTES_REQUIRED,
        ) === 'true';

      this.inputVisitDetails.paragraph.toggleRequirement(apptNotesReq);
    }
  }

  #updateInputInterpreterNeed() {
    this.inputInterpreterNeed.paragraph.toggleRequirement(true);
  }

  #updateInputInsuranceSelection() {
    this.inputInsuranceSelection.paragraph.toggleRequirement(true);

    if (this.viewMode === VIEW_MODES.DEFAULT) {
      const requireInsurance = this.#requireInsurance();
      if (requireInsurance) {
        this.inputInsuranceSelection.paragraph
          .$()
          .attr(ATTRIBUTES.HIDDEN, true);
      } else {
        this.inputInsuranceSelection.paragraph
          .$()
          .removeAttr(ATTRIBUTES.HIDDEN);
      }

      // Default to using insurance
      this.inputInsuranceSelection.paragraph.selectOption(
        INSURANCE_METHOD_TAXONOMY_USE_INSURANCE_ALIAS,
      );
    }

    this.inputInsuranceSelection
      .$()
      .on(DOM_EVENTS.CHANGE, () => this.updateInsuranceRequirements());
  }

  #updateInputInsuranceCardFrontImage() {
    this.inputInsuranceCardFrontImage
      .$()
      .on(DOM_EVENTS.CHANGE, () => this.updateInsuranceRequirements());
  }

  #updateInputInsuranceCardBackImage() {
    this.inputInsuranceCardBackImage
      .$()
      .on(DOM_EVENTS.CHANGE, () => this.updateInsuranceRequirements());
  }

  #updateInputInsuranceProvider() {
    this.inputInsuranceProvider
      .$()
      .on(DOM_EVENTS.CHANGE, () => this.updateInsuranceRequirements());
  }

  #updateInputInsuranceMemberId() {
    this.inputInsuranceMemberId
      .$()
      .on(DOM_EVENTS.INPUT, () => this.updateInsuranceRequirements());
  }

  updateInsuranceRequirements() {
    const useInsurance = this.#useInsurance();
    const requireInsurance = this.#requireInsurance();

    const $insuranceDetails = this.insuranceDetails.$();

    if (useInsurance) {
      $insuranceDetails.removeAttr(ATTRIBUTES.HIDDEN);
    } else {
      $insuranceDetails.attr(ATTRIBUTES.HIDDEN, true);
    }

    if (this.viewMode === VIEW_MODES.DEFAULT) {
      if (requireInsurance) {
        this.disclaimerInsurance.$().attr(ATTRIBUTES.HIDDEN, true);
      }
    }

    const frontImageUploaded = this.inputInsuranceCardFrontImage.paragraph
      .hasValue;
    const backImageUploaded = this.inputInsuranceCardBackImage.paragraph
      .hasValue;
    const insuranceProviderSelected = this.inputInsuranceProvider.paragraph
      .hasValue;
    const memberIdEntered = this.inputInsuranceMemberId.paragraph.hasValue;

    // Front and back image of insurance are not required if insurance provider and member ID are all provided
    const requireInsuranceImages =
      requireInsurance &&
      !(insuranceProviderSelected && memberIdEntered);

    // Insurance provider and member ID are not required if front and back image are both uploaded
    const requireInsuranceInformation =
      requireInsurance && !(frontImageUploaded && backImageUploaded);

    this.inputInsuranceCardFrontImage.paragraph.toggleRequirement(
      requireInsuranceImages,
    );
    this.inputInsuranceCardBackImage.paragraph.toggleRequirement(
      requireInsuranceImages,
    );
    this.legendInsuranceCardImages.paragraph.toggleRequirement(
      requireInsuranceImages,
    );
    this.inputInsuranceProvider.paragraph.toggleRequirement(
      requireInsuranceInformation,
    );
    this.inputInsuranceMemberId.paragraph.toggleRequirement(
      requireInsuranceInformation,
    );

    if (!requireInsuranceImages) {
      this.inputInsuranceCardFrontImage.paragraph.toggleErrorByDisplay(false);
      this.inputInsuranceCardBackImage.paragraph.toggleErrorByDisplay(false);
    }
    if (!requireInsuranceInformation) {
      this.inputInsuranceProvider.paragraph.toggleErrorByDisplay(false);
      this.inputInsuranceMemberId.paragraph.toggleErrorByDisplay(false);
      this.inputInsuranceGroupNumber.paragraph.toggleErrorByDisplay(false);
    }
  }

  #useInsurance() {
    const useInsurance =
      this.inputInsuranceSelection.paragraph.value ===
      INSURANCE_METHOD_TAXONOMY_USE_INSURANCE_ALIAS;
    return useInsurance;
  }

  #requireInsurance() {
    const insuranceRequirement = SessionStorageUtils.getItem(
      SESSION_STORAGE_KEYS.INSURANCE_REQUIREMENT,
    );
    const appointmentRequiresInsurance =
      insuranceRequirement?.toUpperCase() ===
      INSURANCE_REQUIREMENT.INSURANCE_ONLY.toUpperCase();
    const useInsurance = this.#useInsurance();
    return appointmentRequiresInsurance || useInsurance;
  }

  #updateInputReferralRequirement() {
    this.inputReferralRequirement.$().on(DOM_EVENTS.CHANGE, event => {
      const requireReferralAcknowledgement =
        event.target.value === YES_AND_NO_TAXONOMY_YES_ALIAS;
      this.inputReferralAcknowledgement.paragraph.toggleRequirement(
        requireReferralAcknowledgement,
      );
    });
  }

  updateReferralRequirements() {
    const referralRequirement = sessionStorage
      .getItem(SESSION_STORAGE_KEYS.REFERRAL_REQUIREMENT)
      ?.toUpperCase();

    switch (referralRequirement) {
      case REFERRAL_REQUIREMENT.ONLINE: {
        this.disclaimerVisitReason.$().removeAttr(ATTRIBUTES.HIDDEN);
        this.sectionReferralInformation.$().removeAttr(ATTRIBUTES.HIDDEN);
        this.inputReferralRequirement.$().attr(ATTRIBUTES.HIDDEN, true);
        this.inputReferralImage.paragraph.toggleRequirement(true);
        this.inputReferralAcknowledgement.paragraph.toggleRequirement(true);
        AppointmentFormParagraph.referralJumpLink
          .$()
          .removeAttr(ATTRIBUTES.HIDDEN);
        break;
      }
      case REFERRAL_REQUIREMENT.CHECKIN: {
        this.disclaimerVisitReason.$().removeAttr(ATTRIBUTES.HIDDEN);
        this.sectionReferralInformation.$().removeAttr(ATTRIBUTES.HIDDEN);
        this.inputReferralRequirement.paragraph.toggleRequirement(true);
        this.inputReferralAcknowledgement.paragraph.toggleRequirement(true);
        AppointmentFormParagraph.referralJumpLink
          .$()
          .removeAttr(ATTRIBUTES.HIDDEN);
        break;
      }
      case REFERRAL_REQUIREMENT.NONE:
      default: {
        this.disclaimerVisitReason.$().attr(ATTRIBUTES.HIDDEN, true);
        this.sectionReferralInformation.$().attr(ATTRIBUTES.HIDDEN, true);
        AppointmentFormParagraph.referralJumpLink
          .$()
          .removeAttr(ATTRIBUTES.HIDDEN);
        break;
      }
    }
  }

  #updateInputLocationAcknowledgement() {
    this.inputLocationAcknowledgement.paragraph.toggleRequirement(true);
  }

  #hidePhoneTypeVUC() {
    if (this.viewMode === VIEW_MODES.VIRTUAL_URGENT_CARE_OPEN) {
      const MOBILE_PHONE_TYPE_OPTION_VALUE = 'Mobile';
      this.inputPhoneType.paragraph.selectOption(MOBILE_PHONE_TYPE_OPTION_VALUE);
      this.inputPhoneType.$().attr(ATTRIBUTES.HIDDEN, true);
    }
  }

  validate() {
    let valid = super.validate();

    if (this.viewMode === VIEW_MODES.VIRTUAL_URGENT_CARE_OPEN) {
      const month = this.inputMonthNumber.paragraph.validate() ? document.getElementById(this.inputMonthNumber.id).value : null;
      const day = this.inputDayNumber.paragraph.validate() ? document.getElementById(this.inputDayNumber.id).value : null;
      const year = this.inputYearNumber.paragraph.validate() ? document.getElementById(this.inputYearNumber.id).value : null;

      if (month && day && year) {
        const isOver13 = this.checkUnder13(month, day, year);
        if(!isOver13) {
          valid = false;
        }
      }
    }

    if (this.viewMode === VIEW_MODES.DEFAULT) {
      const implicitValues = AppointmentFormParagraph.getImplicitValues();
      const { epicDepartmentId, slot } = implicitValues;

      const $locationBoxWrapper = this.locationBoxWrapper.$();
      if (epicDepartmentId) {
        $locationBoxWrapper.removeClass(CLASS_NAMES.ERROR);
      } else {
        $locationBoxWrapper.addClass(CLASS_NAMES.ERROR);
        valid = false;
      }

      const $dayTimeBoxWrapper = this.dayTimeBoxWrapper.$();
      if (slot) {
        $dayTimeBoxWrapper.removeClass(CLASS_NAMES.ERROR);
      } else {
        $dayTimeBoxWrapper.addClass(CLASS_NAMES.ERROR);
        valid = false;
      }

      AppointmentFormParagraph.#toggleSectionCompletion(
        this.sectionAppointmentDetails,
      );
      AppointmentFormParagraph.#toggleSectionCompletion(
        this.sectionPatientInformation,
      );
      AppointmentFormParagraph.#toggleSectionCompletion(
        this.sectionReferralInformation,
      );
    }

    return valid;
  }

  checkUnder13(month, day, year){
    const dobJsDate = DateUtils.convertYMDToDate(year, month, day);
    const ageInYears = DateUtils.calculateAgeInYears(dobJsDate);

    if (ageInYears < 13) {
      this.inputMonthNumber.paragraph.toggleErrorByDisplay(true);
      this.inputDayNumber.paragraph.toggleErrorByDisplay(true);
      this.inputYearNumber.paragraph.toggleErrorByDisplay(true);
      this.scrollToFirstError();
      return false;
    }

    return true;
  }


  static #toggleSectionCompletion(sectionElement) {
    const $section = sectionElement.$();
    const $h2 = $section.find(HTML_ELEMENTS.H2);
    const errorCount = $section.find(`.${CLASS_NAMES.ERROR}`).length;

    if (errorCount) {
      $h2.removeClass(CLASS_NAMES.SELECTED);
    } else {
      $h2.addClass(CLASS_NAMES.SELECTED);
    }
  }

  getFormObject() {
    let formObject = super.getFormObject();
    const implicitValues = AppointmentFormParagraph.getImplicitValues();
    formObject = { ...formObject, ...implicitValues };

    const month = formObject[this.inputMonthNumber.id];
    const day = formObject[this.inputDayNumber.id];
    const year = formObject[this.inputYearNumber.id];

    if (month && day && year) {
      const dobJsDate = DateUtils.convertYMDToDate(year, month, day);
      formObject.dateOfBirth = dobJsDate.toISOString();
    }

    return formObject;
  }

  /**
   * Fetch session storage and URL parameter values that are needed for form submission.
   *
   * @return {object} a set of key value pairs representing additional form data
   */
  static getImplicitValues() {
    const values = {};

    const url = new URL(window.location.href);
    values.language = url.pathname.split('/')[1];

    const npi = url.searchParams.get(SEARCH_PARAM_KEYS.NPI);
    values.resultType = npi ? RESULT_TYPES.PROVIDER : RESULT_TYPES.LOCATION;

    values.accessibilityType = SessionStorageUtils.getItem(
      SESSION_STORAGE_KEYS.ACCESSIBILITY_TYPE,
    );
    values.reservationKey = SessionStorageUtils.getItem(
      SESSION_STORAGE_KEYS.RESERVATION_KEY,
    );
    values.epicVisitTypeId = SessionStorageUtils.getItem(
      SESSION_STORAGE_KEYS.EPIC_APPOINTMENT_TYPE,
    );

    // This value needs to be from session storage instead of the query string
    // because the datepicker changes the value in session storage and that may
    // be different from the initial query string value.
    values.epicDepartmentId = SessionStorageUtils.getItem(
      SESSION_STORAGE_KEYS.EPIC_DEPARTMENT_ID,
    );

    values.epicProviderId = SessionStorageUtils.getItem(
      SESSION_STORAGE_KEYS.EPIC_PROVIDER_ID,
    );
    values.slot = SessionStorageUtils.getItem(
      SESSION_STORAGE_KEYS.RESERVED_TIME_SLOT,
    );
    values.durationMins = SessionStorageUtils.getItem(
      SESSION_STORAGE_KEYS.DURATION_MINS,
    );
    values.referralRequirement = SessionStorageUtils.getItem(
      SESSION_STORAGE_KEYS.REFERRAL_REQUIREMENT,
    );
    values.insuranceRequirement = SessionStorageUtils.getItem(
      SESSION_STORAGE_KEYS.INSURANCE_REQUIREMENT,
    );
    values.appointmentNotesRequired = sessionStorage.getItem(
      'appointmentNotesRequired',
    );

    return values;
  }

  async submit() {
    const valid = this.validate();
    if (!valid) {
      return;
    }

    if (this.viewMode === VIEW_MODES.VIRTUAL_URGENT_CARE_OPEN) {
      const acceptHandler = () => super.submit();
      this.virtualUrgentCareTermsAndConditionsModal.paragraph.open(
        acceptHandler,
      );
    } else {
      await super.submit();
    }
  }

  async sendData(formObject, errorRedirectPage, successRedirectPage) {
    if (this.viewMode === VIEW_MODES.VIRTUAL_URGENT_CARE_OPEN) {
      await this.#beginVirtualUrgentCareSession(formObject, errorRedirectPage, successRedirectPage);
    } else {
      await this.#sendMercuryHealthData(formObject);
      await this.#scheduleAppointment(formObject, errorRedirectPage, successRedirectPage);
    }
  }

  #scheduleUnauthenticated() {
    const loggedIn = !!this.#myChartUser;

    if (!loggedIn) {
      return true;
    }

    const getCareForSomeoneElseTabSelected = this.tabGetCareForSomeoneElse
      .$()
      .hasClass(CLASS_NAMES.SELECTED);
    return getCareForSomeoneElseTabSelected;
  }

  async #scheduleAppointment(formObject, errorRedirectPage, successRedirectPage) {
    const loggedIn = !!this.#myChartUser;
    const scheduleUnauthenticated = this.#scheduleUnauthenticated();

    const insuranceCardFrontImageBase64 = await this.inputInsuranceCardFrontImage.paragraph.getFileAsBase64String(
      0,
    );
    const insuranceCardBackImageBase64 = await this.inputInsuranceCardBackImage.paragraph.getFileAsBase64String(
      0,
    );
    const referralImageBase64 = await this.inputReferralImage.paragraph.getFileAsBase64String(
      0,
    );

    const isSelfPay =
      formObject[this.inputInsuranceSelection.id] !==
      INSURANCE_METHOD_TAXONOMY_USE_INSURANCE_ALIAS;

    const insurance = new Insurance({
      insuranceCardFrontImage: insuranceCardFrontImageBase64,
      insuranceCardBackImage: insuranceCardBackImageBase64,
      insuranceName: formObject[this.inputInsuranceProvider.id],
      groupNumber: formObject[this.inputInsuranceGroupNumber.id],
      memberNumber: formObject[this.inputInsuranceMemberId.id],
      referralImage: referralImageBase64,
    });

    const isOpenSchedulingAppointment =
      formObject.accessibilityType?.toUpperCase() === ACCESSIBILITY_TYPE.OPEN;

    if (isOpenSchedulingAppointment) {
      if (loggedIn && !scheduleUnauthenticated) {
        const request = {
          reservationKey: formObject.reservationKey,
          visitReasonMnemonic: formObject[this.inputCondition.id],
          epicVisitTypeId: formObject.epicVisitTypeId,
          epicDepartmentId: formObject.epicDepartmentId,
          epicProviderId: formObject.epicProviderId,
          slot: formObject.slot,
          language: formObject.language,
          visitDetails: formObject[this.inputVisitDetails.id],
          insurance,
          isSelfPay,
          referralImage: referralImageBase64,
        };

        try {
          await this.#luminisServiceUtils.scheduleOpenSchedulingAppointmentAuthenticated(
            request, errorRedirectPage, successRedirectPage
          );
        } catch {
          return;
        }
      } else {
        const address = new Address({
          line1: formObject[this.inputStreetAddress.id],
          city: formObject[this.inputCity.id],
          state: formObject[this.inputState.id],
          zipCode: formObject[this.inputZipCode.id],
        });

        const phone = new Phone({
          number: formObject[this.inputPhoneNumber.id],
          type: formObject[this.inputPhoneType.id],
        });

        const patientDemographics = new PatientDemographics({
          firstName: formObject[this.inputFirstName.id],
          preferredName: formObject[this.inputPreferredName.id],
          lastName: formObject[this.inputLastName.id],
          dateOfBirth: formObject.dateOfBirth,
          sex: formObject[this.inputSex.id],
          genderInformation: formObject[this.inputGenderInformation.id],
          email: formObject[this.inputEmail.id],
          phone,
          address,
        });

        const request = {
          reservationKey: formObject.reservationKey,
          visitReasonMnemonic: formObject[this.inputCondition.id],
          epicVisitTypeId: formObject.epicVisitTypeId,
          epicDepartmentId: formObject.epicDepartmentId,
          epicProviderId: formObject.epicProviderId,
          slot: formObject.slot,
          language: formObject.language,
          patientDemographics,
          preferredContactMethod:
            formObject[this.inputPreferredContactMethod.id],
          visitDetails: formObject[this.inputVisitDetails.id],
          insurance,
          isSelfPay,
          referralImage: referralImageBase64,
        };

        try {
          await this.#luminisServiceUtils.scheduleOpenSchedulingAppointmentUnauthenticated(
            request, errorRedirectPage, successRedirectPage
          );
        } catch {
          return;
        }
      }
    } else {
      const request = {
        reservationKey: formObject.reservationKey,
        visitReasonMnemonic: formObject[this.inputCondition.id],
        epicVisitTypeId: formObject.epicVisitTypeId,
        epicDepartmentId: formObject.epicDepartmentId,
        epicProviderId: formObject.epicProviderId,
        slot: formObject.slot,
        durationMins: formObject.durationMins,
        language: formObject.language,
        visitDetails: formObject[this.inputVisitDetails.id],
        insurance,
        isSelfPay,
        referralImage: referralImageBase64,
      };

      try {
        await this.#luminisServiceUtils.scheduleDirectSchedulingAppointment(
          request, errorRedirectPage, successRedirectPage
        );
      } catch {
        return;
      }
    }

    sessionStorage.setItem(
      SESSION_STORAGE_KEYS.MNEMONIC,
      formObject[this.inputCondition.id],
    );

    const humanReadableMnemonic = this.inputCondition
      .$()
      .find(`:${ATTRIBUTES.SELECTED}`)
      .text();
    sessionStorage.setItem(
      SESSION_STORAGE_KEYS.RECENT_CONDITION,
      humanReadableMnemonic,
    );

    sessionStorage.setItem(
      SESSION_STORAGE_KEYS.RESULT_TYPE,
      formObject.resultType,
    );

    if (this.#myChartUser) {
      formObject[
        this.inputEmail.id
      ] = await this.#luminisServiceUtils.getPatientEmail();
    }

    sessionStorage.setItem(
      SESSION_STORAGE_KEYS.EMAIL,
      formObject[this.inputEmail.id],
    );
  }

  async #beginVirtualUrgentCareSession(formObject, errorRedirectPage, successRedirectPage) {
    let response;

    const loggedIn = !!this.#myChartUser;
    const scheduleUnauthenticated = this.#scheduleUnauthenticated();

    const interpreterNeeded =
      formObject[this.inputInterpreterNeed.id] ===
      YES_AND_NO_TAXONOMY_YES_ALIAS;

    const isSelfPay =
      formObject[this.inputInsuranceSelection.id] !==
      INSURANCE_METHOD_TAXONOMY_USE_INSURANCE_ALIAS;

    const insuranceCardFrontImageBase64 = await this.inputInsuranceCardFrontImage.paragraph.getFileAsBase64String(
      0,
    );
    const insuranceCardBackImageBase64 = await this.inputInsuranceCardBackImage.paragraph.getFileAsBase64String(
      0,
    );

    const insurance = new Insurance({
      insuranceCardFrontImage: insuranceCardFrontImageBase64,
      insuranceCardBackImage: insuranceCardBackImageBase64,
      insuranceName: formObject[this.inputInsuranceProvider.id],
      groupNumber: formObject[this.inputInsuranceGroupNumber.id],
      memberNumber: formObject[this.inputInsuranceMemberId.id],
    });

    if (loggedIn && !scheduleUnauthenticated) {
      const request = {
        visitDetails: formObject[this.inputVisitDetails.id],
        interpreterNeeded,
        insurance,
        isSelfPay,
      };

      try {
        response = await this.#luminisServiceUtils.beginVirtualUrgentCareSessionAuthenticated(
          request, errorRedirectPage, successRedirectPage
        );
      } catch {
        return;
      }
    } else {
      const address = new Address({
        line1: formObject[this.inputStreetAddress.id],
        city: formObject[this.inputCity.id],
        state: formObject[this.inputState.id],
        zipCode: formObject[this.inputZipCode.id],
      });

      const phone = new Phone({
        number: formObject[this.inputPhoneNumber.id],
        type: formObject[this.inputPhoneType.id],
      });

      const patientDemographics = new PatientDemographics({
        firstName: formObject[this.inputFirstName.id],
        preferredName: formObject[this.inputPreferredName.id],
        lastName: formObject[this.inputLastName.id],
        dateOfBirth: formObject.dateOfBirth,
        sex: formObject[this.inputSex.id],
        genderInformation: formObject[this.inputGenderInformation.id],
        email: formObject[this.inputEmail.id],
        phone,
        address,
      });

      const request = {
        patientDemographics,
        visitDetails: formObject[this.inputVisitDetails.id],
        interpreterNeeded,
        insurance,
        isSelfPay,
      };

      try {
        response = await this.#luminisServiceUtils.beginVirtualUrgentCareSessionUnauthenticated(
          request, errorRedirectPage, successRedirectPage
        );
      } catch {
        return;
      }
    }

    sessionStorage.setItem(
      SESSION_STORAGE_KEYS.VIRTUAL_URGENT_CARE_SESSION_ID,
      response.sessionId,
    );
  }

  async #sendMercuryHealthData(formObject) {
    const basePath = this.mercuryHealthBlock.submissionUrl.anchor
      .$(0)
      .attr(ATTRIBUTES.HREF);
    const httpClient = new HttpClient(basePath);
    const eloquaServiceUtils = new EloquaServiceUtils(httpClient);

    try {
      const address = new Address({
        line1: formObject[this.inputStreetAddress.id],
        city: formObject[this.inputCity.id],
        state: formObject[this.inputState.id],
        zipCode: formObject[this.inputZipCode.id],
      });

      const phone = new Phone({
        number: formObject[this.inputPhoneNumber.id],
        type: formObject[this.inputPhoneType.id],
      });

      const patientDemographics = new PatientDemographics({
        firstName: formObject[this.inputFirstName.id],
        lastName: formObject[this.inputLastName.id],
        preferredName: formObject[this.inputPreferredName.id],
        dateOfBirth: formObject.dateOfBirth,
        sex: formObject[this.inputSex.id],
        genderInformation: formObject[this.inputGenderInformation.id],
        email: formObject[this.inputEmail.id],
        phone,
        address,
      });

      const insurance = new Insurance({
        insuranceName: formObject[this.inputInsuranceProvider.id],
        groupNumber: formObject[this.inputInsuranceGroupNumber.id],
        memberNumber: formObject[this.inputInsuranceMemberId.id],
      });

      const request = {
        visitReasonMnemonic: formObject[this.inputCondition.id],
        epicDepartmentId: formObject.epicDepartmentId,
        slot: formObject.slot,
        epicVisitTypeId: formObject.epicVisitTypeId,
        patientDemographics,
        preferredContactMethod: formObject[this.inputPreferredContactMethod.id],
        visitDetails: formObject[this.inputVisitDetails.id],
        insurance,
        referralRequirement: formObject.referralRequirement,
      };

      await eloquaServiceUtils.sendAppointmentForm(request);
    } catch (error) {
      // We don't really care if MH fails it should not stop everything.
      // eslint-disable-next-line no-console
      console.error(error);
    }
  }
}
